import * as z from 'zod'
import { ZodLiteral, ZodOptional, ZodString, ZodUnion } from 'zod'
import {
  ADDRESS_REGEX,
  CA_POSTAL_REGEX,
  CITY_REGEX,
  COMPANY_NAME_REGEX,
  countryRegionOptions,
  DropDownValue,
  EMAIL_REGEX,
  FIN_REGEX,
  NAME_REGEX,
  PHONE_REGEX,
  ZIP_REGEX,
} from '../../utils/constants'
import { CountryCode } from '../../types/locale'
import { TFunction } from 'i18next'
import { ShallowNestedKeys } from '../../types/general'
import { LithiumNamespace } from '../../utils/i18n'

export type CustomerInfoFormInput = {
  firstName: string
  lastName: string
  companyLegalName: string
  finCode: string
  email: string
  phoneNumber: {
    formattedValue: string
    value: string
    isValid: boolean
  }
  streetAddress1: string
  streetAddress2: string
  city: string
  country: DropDownValue<CountryCode>
  stateProvince: DropDownValue<string>
  postalCode: string
}
export type CustomerInfoFormKeys = ShallowNestedKeys<CustomerInfoFormInput>

export function getCustomerInfoFormSchema(
  t: TFunction<LithiumNamespace, undefined>,
  countryCode: CountryCode,
) {
  return z.object({
    firstName: buildFieldSchema(t, t('customerInfo.form.givenName'), {
      regex: NAME_REGEX,
    }),
    lastName: buildFieldSchema(t, t('customerInfo.form.surname'), {
      regex: NAME_REGEX,
    }),
    companyLegalName: buildFieldSchema(
      t,
      t('customerInfo.form.companyLegalName'),
      { regex: COMPANY_NAME_REGEX },
    ),
    finCode: buildFieldSchema(t, t('customerInfo.form.fin'), {
      regex: FIN_REGEX,
      maxLength: 5,
      optional: true,
    }),
    email: buildFieldSchema(t, t('customerInfo.form.email'), {
      regex: EMAIL_REGEX,
    }),
    phoneNumber: z.object({
      formattedValue: buildFieldSchema(t, t('customerInfo.form.phoneNumber'), {
        minLength: 10,
      }),
      value: buildFieldSchema(t, t('customerInfo.form.phoneNumber'), {
        regex: PHONE_REGEX,
        minLength: 10,
      }),
      isValid: z.boolean().refine((value) => value, {
        message: t('customerInfo.form.validation.invalid', {
          fieldName: t('customerInfo.form.phoneNumber'),
        }),
      }),
    }),
    streetAddress1: buildFieldSchema(t, t('customerInfo.form.address'), {
      regex: ADDRESS_REGEX,
      minLength: 3,
    }),
    streetAddress2: buildFieldSchema(t, t('customerInfo.form.addressLine2'), {
      regex: ADDRESS_REGEX,
      optional: true,
    }),
    city: buildFieldSchema(t, t('customerInfo.form.city'), {
      regex: CITY_REGEX,
      minLength: 1,
    }),
    country: z.object({
      label: buildFieldSchema(t, t('customerInfo.form.country'), {
        minLength: 1,
      }),
      value: buildFieldSchema(t, t('customerInfo.form.country'), {
        minLength: 1,
      }),
    }),
    stateProvince: z.object({
      label: buildFieldSchema(t, t('customerInfo.form.stateProvince'), {
        minLength: 1,
      }),
      value: buildFieldSchema(t, t('customerInfo.form.stateProvince'), {
        minLength: 1,
      }),
    }),
    postalCode: buildFieldSchema(t, t('customerInfo.form.postalCode'), {
      regex: countryCode === 'CA' ? CA_POSTAL_REGEX : ZIP_REGEX,
      minLength: 5,
    }),
  })
}

function buildFieldSchema(
  t: TFunction<LithiumNamespace, undefined>,
  fieldName: string,
  options: {
    regex?: RegExp
    minLength?: number
    maxLength?: number
    optional?: boolean
  },
) {
  const minLength = options.minLength ?? 2
  const maxLength = options.maxLength ?? 40
  const optional = options.optional || false

  let schema: ZodString | ZodUnion<[ZodOptional<ZodString>, ZodLiteral<''>]> = z
    .string()
    .max(maxLength, t('customerInfo.form.validation.tooLong', { fieldName }))

  if (options.regex) {
    schema = schema.regex(
      options.regex,
      t('customerInfo.form.validation.invalid', { fieldName }),
    )
  }

  if (optional) {
    schema = schema.optional().or(z.literal(''))
  } else {
    schema = schema.min(
      minLength,
      t('customerInfo.form.validation.required', { fieldName }),
    )
  }

  return schema
}

export function getEmptyCustomerInfo(
  t: TFunction<LithiumNamespace, undefined>,
  countryCode: CountryCode,
): CustomerInfoFormInput {
  return {
    firstName: '',
    lastName: '',
    companyLegalName: '',
    finCode: '',
    email: '',
    phoneNumber: {
      formattedValue: '',
      value: '',
      isValid: false,
    },
    streetAddress1: '',
    streetAddress2: '',
    city: '',
    country:
      countryRegionOptions.find((opt) => opt.value === countryCode) ||
      countryRegionOptions[0],
    stateProvince: {
      label: t('customerInfo.form.stateProvince'),
      value: '',
    },
    postalCode: '',
  }
}
